import React, { useState, useEffect, useMemo } from 'react';
import { useVacation } from '../../contexts/VacationContext';
import { useStaff } from '../../contexts/StaffContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useAuth } from '../../contexts/AuthContext';
import { VacationHelpers } from '../../models/VacationModels';
import { formatDurationFromMinutes } from '../../utils/TimeSlotUtils';
import '../../styles/vacation-modal.css';

/**
 * VacationModal Component
 * 
 * Modal for adding or editing vacation entries.
 * Supports both single date and date range selection.
 * Shows real-time preview of hours calculation based on shifts.
 * 
 */
const VacationModal = ({
  isOpen,
  onClose,
  mode = 'add',
  vacationData = null,
  preselectedStaffId = null,
  preselectedDepartmentId = null,
  preselectedDate = null
}) => {
  const { currentUser } = useAuth();
  const { addVacation, addVacationRange, updateVacation, previewVacationRange, loading } = useVacation();
  const { activeStaff: staffMembers } = useStaff();
  const { departments } = useDepartment();

  // Form state
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [dateSelectionMode, setDateSelectionMode] = useState('single'); // 'single' or 'range'
  const [singleDate, setSingleDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  // Local loading state
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Initialize form data on mount or when props change
   */
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && vacationData) {
        // Edit mode - populate with existing data
        setSelectedStaffId(vacationData.staffId);
        setSelectedDepartmentId(vacationData.departmentId);
        setDateSelectionMode('single');
        setSingleDate(formatDateForInput(vacationData.date));
      } else {
        // Add mode - use preselected values
        setSelectedStaffId(preselectedStaffId || '');
        setSelectedDepartmentId(preselectedDepartmentId || '');
        setDateSelectionMode('single');
        setSingleDate(preselectedDate ? formatDateForInput(preselectedDate) : '');
        setStartDate('');
        setEndDate('');
      }
      
      setPreview(null);
      setErrors({});
      setShowValidation(false);
    }
  }, [isOpen, mode, vacationData, preselectedStaffId, preselectedDepartmentId, preselectedDate]);

  /**
   * Filter staff by selected department
   */
  const filteredStaff = useMemo(() => {
    if (!selectedDepartmentId) {
      return staffMembers;
    }
    return staffMembers.filter(staff => 
      staff.departmentIds?.includes(selectedDepartmentId)
    );
  }, [staffMembers, selectedDepartmentId]);

  /**
   * Auto-select department when staff is selected
   */
  useEffect(() => {
    if (selectedStaffId && !selectedDepartmentId) {
      const staff = staffMembers.find(s => s.id === selectedStaffId);
      if (staff?.primaryDepartmentId) {
        setSelectedDepartmentId(staff.primaryDepartmentId);
      } else if (staff?.departmentIds?.length > 0) {
        setSelectedDepartmentId(staff.departmentIds[0]);
      }
    }
  }, [selectedStaffId, selectedDepartmentId, staffMembers]);

  /**
   * Generate preview when date range changes
   */
  useEffect(() => {
    if (dateSelectionMode === 'range' && startDate && endDate && selectedStaffId && selectedDepartmentId) {
      generatePreview();
    } else {
      setPreview(null);
    }
  }, [dateSelectionMode, startDate, endDate, selectedStaffId, selectedDepartmentId]);

  /**
   * Format date for input field (YYYY-MM-DD)
   */
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toISOString().split('T')[0];
  };

  /**
   * Generate hours preview for date range
   */
  const generatePreview = async () => {
    if (!startDate || !endDate || !selectedStaffId || !selectedDepartmentId) {
      return;
    }

    setPreviewLoading(true);
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const previewData = await previewVacationRange(
        selectedStaffId,
        selectedDepartmentId,
        start,
        end
      );
      
      setPreview(previewData);
    } catch (error) {
      console.error('Error generating preview:', error);
      setErrors(prev => ({ ...prev, preview: 'Fehler beim Generieren der Vorschau' }));
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    if (!selectedStaffId) {
      newErrors.staff = 'Mitarbeiter ist erforderlich';
    }

    if (!selectedDepartmentId) {
      newErrors.department = 'Abteilung ist erforderlich';
    }

    if (dateSelectionMode === 'single') {
      if (!singleDate) {
        newErrors.date = 'Datum ist erforderlich';
      }
    } else {
      if (!startDate) {
        newErrors.startDate = 'Startdatum ist erforderlich';
      }
      if (!endDate) {
        newErrors.endDate = 'Enddatum ist erforderlich';
      }
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        newErrors.dateRange = 'Enddatum muss nach Startdatum liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (mode === 'edit') {
        // Update existing vacation
        await updateVacation(vacationData.id, {
          date: new Date(singleDate)
        });
      } else {
        // Add new vacation(s)
        if (dateSelectionMode === 'single') {
          await addVacation({
            staffId: selectedStaffId,
            departmentId: selectedDepartmentId,
            date: new Date(singleDate),
            createdBy: currentUser.uid
          });
        } else {
          await addVacationRange(
            selectedStaffId,
            selectedDepartmentId,
            new Date(startDate),
            new Date(endDate)
          );
        }
      }

      // Success - close modal
      onClose();
    } catch (error) {
      console.error('Error saving vacation:', error);
      setErrors({ submit: error.message || 'Fehler beim Speichern des Urlaubs' });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="vacation-modal-overlay" onClick={handleBackdropClick}>
      <div className="vacation-modal">
        {/* Header */}
        <div className="vacation-modal-header">
          <h2>
            {mode === 'edit' ? 'Urlaub bearbeiten' : 'Urlaub hinzufügen'}
          </h2>
          <button
            type="button"
            className="vacation-modal-close"
            onClick={handleClose}
            disabled={isSaving}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="vacation-modal-form">
          {/* Staff Selection */}
          <div className="form-group">
            <label htmlFor="staff-select">
              Mitarbeiter <span className="required">*</span>
            </label>
            <select
              id="staff-select"
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              disabled={mode === 'edit' || isSaving}
              className={showValidation && errors.staff ? 'error' : ''}
            >
              <option value="">Mitarbeiter auswählen</option>
              {filteredStaff.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
            {showValidation && errors.staff && (
              <span className="error-message">{errors.staff}</span>
            )}
          </div>

          {/* Department Selection */}
          <div className="form-group">
            <label htmlFor="department-select">
              Abteilung <span className="required">*</span>
            </label>
            <select
              id="department-select"
              value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(e.target.value)}
              disabled={mode === 'edit' || isSaving}
              className={showValidation && errors.department ? 'error' : ''}
            >
              <option value="">Abteilung auswählen</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            {showValidation && errors.department && (
              <span className="error-message">{errors.department}</span>
            )}
          </div>

          {/* Date Selection Mode (only in add mode) */}
          {mode === 'add' && (
            <div className="form-group">
              <label>Datum auswählen</label>
              <div className="date-mode-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${dateSelectionMode === 'single' ? 'active' : ''}`}
                  onClick={() => setDateSelectionMode('single')}
                  disabled={isSaving}
                >
                  Einzelnes Datum
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${dateSelectionMode === 'range' ? 'active' : ''}`}
                  onClick={() => setDateSelectionMode('range')}
                  disabled={isSaving}
                >
                  Datumsbereich
                </button>
              </div>
            </div>
          )}

          {/* Single Date Picker */}
          {dateSelectionMode === 'single' && (
            <div className="form-group">
              <label htmlFor="single-date">
                Datum <span className="required">*</span>
              </label>
              <input
                type="date"
                id="single-date"
                value={singleDate}
                onChange={(e) => setSingleDate(e.target.value)}
                disabled={isSaving}
                className={showValidation && errors.date ? 'error' : ''}
              />
              {showValidation && errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>
          )}

          {/* Date Range Picker */}
          {dateSelectionMode === 'range' && (
            <>
              <div className="form-group-row">
                <div className="form-group">
                  <label htmlFor="start-date">
                    Von <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    id="start-date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isSaving}
                    className={showValidation && errors.startDate ? 'error' : ''}
                  />
                  {showValidation && errors.startDate && (
                    <span className="error-message">{errors.startDate}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="end-date">
                    Bis <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    id="end-date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isSaving}
                    className={showValidation && errors.endDate ? 'error' : ''}
                  />
                  {showValidation && errors.endDate && (
                    <span className="error-message">{errors.endDate}</span>
                  )}
                </div>
              </div>

              {showValidation && errors.dateRange && (
                <span className="error-message">{errors.dateRange}</span>
              )}
            </>
          )}

          {/* Preview Section (for date range) */}
          {dateSelectionMode === 'range' && preview && (
            <div className="vacation-preview">
              <h3>Vorschau der Urlaubstage</h3>
              
              {previewLoading ? (
                <div className="preview-loading">Berechne Stunden...</div>
              ) : (
                <>
                  <div className="preview-list">
                    {preview.preview.map((item, index) => (
                      <div key={index} className="preview-item">
                        <span className="preview-date">{item.dateString}</span>
                        <span className={`preview-hours ${!item.hasShift ? 'no-shift' : ''}`}>
                          {item.durationMinutes > 0 
                            ? formatDurationFromMinutes(item.durationMinutes)
                            : '0h (Kein Dienst geplant)'
                          }
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="preview-summary">
                    <strong>
                      {preview.totalDays} Tage, {formatDurationFromMinutes(preview.totalMinutes)}
                    </strong>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Error Message */}
          {errors.submit && (
            <div className="error-banner">
              {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="vacation-modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isSaving}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving || loading}
            >
              {isSaving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VacationModal;